import {
  MUIAccordion,
  MUIButton,
  MUIDialog,
  MUISelect,
  MUISwitch,
  MUITooltip,
} from "@abb/arcadia-ui-react"

import { StatusCodes } from "http-status-codes"
import { JSX, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../services/api"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import { ControllerRendererProps } from "../../../../types/reactHookForm"
import {
  AccessLevel,
  ApplicationAccessLevel,
  ApplicationAccessLevelEdit,
  ApplicationFeaturesAccessLevel,
  DimaFeature,
  DimaFeatureShortNameEnum,
  FeatureAccessLevel,
  FeatureAccessLevelEdit,
  UserAuthorizations,
  canUserGrantAccessToDima,
  getAppFeatureAccessLevelByAppShortName,
  getFeatureAccessLevel,
  hasUserAccessToDima,
} from "../../../../models/Authorization"
import "./UserBackofficeAccess.scss"

const returnURL = "/users"

export function UserBackofficeAccess() {
  const { user, isLoadingUser } = useContext(UserContext)

  const { userId } = useParams<string>()
  const navigate = useNavigate()

  const [selectedUserHasAccessToDima, setSelectedUserHasAccessToDima] =
    useState<boolean>(false)
  const [isLoadingAuthorizations, setIsLoadingAuthorizations] = useState<boolean>(true)
  const [selectedUserApplicationsAccesses, setSelectedUserApplicationsAccesses] =
    useState<ApplicationAccessLevel[]>()
  const [selectedUserFeaturesAccesses, setSelectedUserFeaturesAccesses] =
    useState<FeatureAccessLevel[]>()
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState<boolean>(false)
  const [isChangingAccess, setIsChangingAccess] = useState<boolean>(false)
  const [accessJustEnabled, setAccessJustEnabled] = useState<boolean>(false)

  const [initialAppFeaturesVal, setInitialAppFeaturesVal] = useState<{
    [key: string]: ApplicationFeaturesAccessLevel
  }>({})

  const deleteUserAuthorizations = useCallback(
    () =>
      digitalManagerApi.delete(`/admin/authorizations/${userId}/`).then(() => {
        setSelectedUserHasAccessToDima(false)
        setAccessJustEnabled(false)
      }),
    [userId]
  )

  function createRenderField(
    getTitleFunction: (
      selectedUserHigherThanCurrent: boolean,
      currentUserHasReadAccessLevel: boolean
    ) => string,
    selectedUserHigherThanCurrent: boolean,
    currentUserHasReadAccessLevel: boolean,
    feature: string,
    userBackofficeAccessLevel: AccessLevel,
    currentUserFeatureAccessLevel: AccessLevel,
    includeFeatureCheck: boolean
  ) {
    return function renderField({
      field,
    }: ControllerRendererProps): JSX.Element {
      return (
        <MUITooltip
          tooltipText={getTitleFunction(
            selectedUserHigherThanCurrent,
            currentUserHasReadAccessLevel
          )}
        >
          <MUISelect
            name={field.name}
            size={32}
            dropdownLOV={Object.keys(AccessLevel)
              .map((k) => Number.parseInt(k, 10))
              .filter((n) => !Number.isNaN(n))
              .map((levelNumber) => ({
                id: String(levelNumber),
                name: String(AccessLevel[levelNumber]),
                isDisabled: currentUserFeatureAccessLevel < levelNumber,
              }))}
            selectedItem={
              field.value !== undefined && field.value !== null
                ? [String(field.value)]
                : []
            }
            onChange={handleAccessLevelChange(field)}
            onBlur={field.onBlur}
            isDisabled={
              selectedUserHigherThanCurrent ||
              currentUserHasReadAccessLevel ||
              (includeFeatureCheck && feature === DimaFeatureShortNameEnum.RR) ||
              userBackofficeAccessLevel <= AccessLevel.Read
            }
          />
        </MUITooltip>
      )
    }
  }

  useEffect(() => {
    if (userId) {
      digitalManagerApi
        .get<UserAuthorizations>(`/admin/authorizations/${userId}`)
        .then((res) => {
          if (res.status === StatusCodes.OK && res.data) {
            setSelectedUserHasAccessToDima(hasUserAccessToDima(res.data))
            setSelectedUserApplicationsAccesses(res.data.applications)
            setSelectedUserFeaturesAccesses(res.data.features)
          }
        })
        .catch(() => {
          navigate(returnURL)
        })
        .finally(() => {
          setIsLoadingAuthorizations(false)
        })
    }
  }, [navigate, userId])

  const initialValues = useMemo(() => {
    if (!user?.authorizations) {
      return {}
    }

    const values: { [key: string]: ApplicationFeaturesAccessLevel } = {}

    if (selectedUserApplicationsAccesses) {
      user.authorizations.applications.forEach((app: ApplicationAccessLevel) => {
        const appValues: ApplicationFeaturesAccessLevel = {}

        Object.keys(app.featuresAccesses)
          .filter(
            (feature) =>
              !feature.includes("$") &&
              getAppFeatureAccessLevelByAppShortName(
                app.shortName,
                feature,
                user.authorizations.applications
              ) >= AccessLevel.Read &&
              feature !== DimaFeatureShortNameEnum.RR
          )
          .forEach((feature) => {
            if (!DimaFeature[feature]) {
              return
            }

            appValues[feature] = getAppFeatureAccessLevelByAppShortName(
              app.shortName,
              feature,
              selectedUserApplicationsAccesses
            )
          })

        values[app.id] = appValues
      })
    }

    const featureValues: ApplicationFeaturesAccessLevel = {}

    if (selectedUserFeaturesAccesses) {
      user.authorizations.features.forEach((feature: FeatureAccessLevel) => {
        if (!DimaFeature[feature.shortName]) {
          return
        }

        featureValues[feature.id] = getFeatureAccessLevel(
          feature.shortName,
          selectedUserFeaturesAccesses
        )
      })
    }

    values.features = featureValues

    return values
  }, [selectedUserApplicationsAccesses, selectedUserFeaturesAccesses, user])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
    setValue,
  } = useForm<{
    [key: string]: ApplicationFeaturesAccessLevel
  }>()

  useEffect(() => {
    setInitialAppFeaturesVal(initialValues)
    reset(initialValues)
  }, [initialValues, reset])

  const changeAccessToDima = useCallback(() => {
    const rrFeature = user?.authorizations?.features?.find(
      (feature) => feature.shortName === DimaFeatureShortNameEnum.RR
    )

    setSelectedUserHasAccessToDima(true)

    if (rrFeature) {
      setValue(`features.${rrFeature.id}`, AccessLevel.Read, {
        shouldDirty: true,
      })
      setAccessJustEnabled(true)
    }
  }, [setValue, user])

  const handleAccessLevelChange = useCallback(
    (field: ControllerRenderProps) => (values: Array<string | { id: string }>) => {
      const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
      const value = typeof first === "string" ? first : (first?.id ?? "")
      field.onChange(value ? Number.parseInt(value, 10) : value)
    },
    []
  )

  const handleAccessToDimaChange = useCallback(() => {
    if (selectedUserHasAccessToDima) {
      setConfirmRevokeOpen(true)
    } else {
      changeAccessToDima()
    }
  }, [selectedUserHasAccessToDima, changeAccessToDima])

  const onSubmit = async (values: { [key: string]: ApplicationFeaturesAccessLevel }) => {
    const appsAuthorizations: ApplicationAccessLevelEdit[] = []
    Object.keys(values)
      .filter((feature) => !feature.includes("features"))
      .forEach((id) => {
        if (JSON.stringify(initialAppFeaturesVal[id]) !== JSON.stringify(values[id])) {
          const ModifiedAppFeaturesVal: ApplicationFeaturesAccessLevel = {}

          Object.keys(values[id]).forEach((feature) => {
            if (values[id][feature] !== initialAppFeaturesVal[id][feature]) {
              ModifiedAppFeaturesVal[feature] = values[id][feature]
            }
          })

          appsAuthorizations.push({
            id,
            featuresAccesses: ModifiedAppFeaturesVal,
          })
        }
      })

    const featuresAuthorizations: FeatureAccessLevelEdit[] = []

    if (values.features) {
      Object.keys(values.features).forEach((id) => {
        if (initialAppFeaturesVal.features?.[id] !== values.features[id]) {
          featuresAuthorizations.push({
            id,
            accessLevel: values.features[id],
          })
        }
      })
    }

    try {
      const res = await digitalManagerApi.put(`/admin/authorizations/${userId}`, {
        applications: appsAuthorizations,
        features: featuresAuthorizations,
      })
      if (res.status === StatusCodes.OK) {
        setSelectedUserHasAccessToDima(hasUserAccessToDima(res.data))
        setSelectedUserApplicationsAccesses(res.data.applications)
        setSelectedUserFeaturesAccesses(res.data.features)
        setAccessJustEnabled(false)
        toast.success("Accesses to backoffice features updated")
      }
    } catch {
      toast.error("Cannot update accesses to backoffice features")
    }
  }

  const setConfirmRevokeOpenFalse = useCallback(() => {
    setConfirmRevokeOpen(false)
  }, [])

  const onClick = useCallback(() => {
    setIsChangingAccess(true)
    deleteUserAuthorizations()
      .then(() => {
        toast.success("User has no longer access")
      })
      .catch(() => {
        toast.error(
          "You can't revoke access to Backoffice, because this user has access to some features you don't have access to or has an approval role."
        )
      })
      .finally(() => {
        setConfirmRevokeOpen(false)
        setIsChangingAccess(false)
      })
  }, [deleteUserAuthorizations])

  const userCanGiveAccessToDima = canUserGrantAccessToDima(
    user?.authorizations ?? { applications: [], features: [] }
  )

  const UserBackofficeAccessLevel = getFeatureAccessLevel(
    DimaFeatureShortNameEnum.BA,
    (user?.authorizations ?? { applications: [], features: [] }).features
  )

  const getTitle = useCallback(
    (selectedUserHigherThanCurrent: boolean, currentUserHasReadAccessLevel: boolean) => {
      if (selectedUserHigherThanCurrent) {
        return "You cannot change access level, because this user has higher privileges than you"
      }
      if (currentUserHasReadAccessLevel) {
        return "You cannot change access level, because you have only read access level on this feature"
      }
      return ""
    },
    []
  )

  if (!userId) {
    return <Navigate to={returnURL} />
  }

  if (isLoadingUser || isLoadingAuthorizations || !user) {
    return <DiMaProgress />
  }

  const appsList: Array<JSX.Element> = []
  if (user.authorizations && selectedUserApplicationsAccesses) {
    user.authorizations.applications.forEach((app: ApplicationAccessLevel) => {
      const appFeaturesControls: Array<JSX.Element> = []

      Object.keys(app.featuresAccesses)
        .filter(
          (feature) =>
            !feature.includes("$") &&
            getAppFeatureAccessLevelByAppShortName(
              app.shortName,
              feature,
              user.authorizations.applications
            ) >= AccessLevel.Read &&
            feature !== DimaFeatureShortNameEnum.RR
        )
        .forEach((feature) => {
          if (!DimaFeature[feature]) {
            appFeaturesControls.push(
              <div key={`spacer-${feature}`} style={{ height: "16px" }} />
            )
            return
          }

          const selectedUserFeaturesAccessesLevel =
            getAppFeatureAccessLevelByAppShortName(
              app.shortName,
              feature,
              selectedUserApplicationsAccesses
            )
          const currentUserFeatureAccessLevel = app.featuresAccesses[feature]
          const selectedUserHigherThanCurrent =
            selectedUserFeaturesAccessesLevel > currentUserFeatureAccessLevel
          const currentUserHasReadAccessLevel =
            currentUserFeatureAccessLevel === AccessLevel.Read

          const selectId = `${app.id}.${feature}`

          appFeaturesControls.push(
            <div
              key={selectId}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(12, 1fr)",
                gap: 1,
                alignItems: "center",
                padding: "8px 0",
              }}
            >
              <div
                style={{
                  gridColumn: "span 4",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {DimaFeature[feature]}
              </div>
              <div
                style={{ gridColumn: "span 6", display: "flex", alignItems: "center" }}
              >
                <Controller
                  name={selectId}
                  control={control}
                  defaultValue={
                    selectedUserFeaturesAccessesLevel as unknown as ApplicationFeaturesAccessLevel
                  }
                  render={createRenderField(
                    getTitle,
                    selectedUserHigherThanCurrent,
                    currentUserHasReadAccessLevel,
                    feature,
                    UserBackofficeAccessLevel,
                    currentUserFeatureAccessLevel,
                    true
                  )}
                />
              </div>
            </div>
          )
        })

      appsList.push(
        <MUIAccordion
          key={app.shortName}
          id={`accordion-${app.shortName}`}
          accordionItems={[
            {
              id: String(app.shortName),
              headerLeftPart: (
                <span style={{ width: "33%", flexShrink: 0 }}>{app.name}</span>
              ),
              accordionContent: <div>{appFeaturesControls}</div>,
              defaultState: "open",
            },
          ]}
        />
      )
    })

    const featuresControls: Array<JSX.Element> = []

    user.authorizations.features.forEach((feature: FeatureAccessLevel) => {
      if (!DimaFeature[feature.shortName]) {
        featuresControls.push(
          <div key={`spacer-${feature.id}`} style={{ height: "16px" }} />
        )
        return
      }

      const selectedUserFeaturesAccessesLevel = getFeatureAccessLevel(
        feature.shortName,
        selectedUserFeaturesAccesses
      )
      const currentUserFeatureAccessLevel = feature.accessLevel
      const selectedUserHigherThanCurrent =
        selectedUserFeaturesAccessesLevel > currentUserFeatureAccessLevel
      const currentUserHasReadAccessLevel =
        currentUserFeatureAccessLevel === AccessLevel.Read

      const selectId = `features.${feature.id}`

      featuresControls.push(
        <div
          key={selectId}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: 1,
            alignItems: "center",
            padding: "8px 0",
          }}
        >
          <div
            style={{
              gridColumn: "span 4",
              display: "flex",
              alignItems: "center",
            }}
          >
            {DimaFeature[feature.shortName]}
          </div>
          <div style={{ gridColumn: "span 6", display: "flex", alignItems: "center" }}>
            <Controller
              name={selectId}
              control={control}
              defaultValue={
                selectedUserFeaturesAccessesLevel as unknown as ApplicationFeaturesAccessLevel
              }
              render={createRenderField(
                getTitle,
                selectedUserHigherThanCurrent,
                currentUserHasReadAccessLevel,
                feature.shortName,
                UserBackofficeAccessLevel,
                currentUserFeatureAccessLevel,
                false
              )}
            />
          </div>
        </div>
      )
    })

    if (featuresControls.length) {
      appsList.push(
        <MUIAccordion
          key="other"
          id="accordion-other"
          accordionItems={[
            {
              id: "other",
              headerLeftPart: (
                <span style={{ width: "33%", flexShrink: 0 }}>Other features</span>
              ),
              accordionContent: <div>{featuresControls}</div>,
              defaultState: "open",
            },
          ]}
        />
      )
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            alignItems: "center",
            marginTop: "16px",
          }}
        >
          <MUISwitch
            size={24}
            checked={selectedUserHasAccessToDima}
            defaultIconContent="\ead2"
            handleChange={handleAccessToDimaChange}
            disabled={
              !userCanGiveAccessToDima || UserBackofficeAccessLevel <= AccessLevel.Read
            }
          />
          <p className="text-body1">
            The user {selectedUserHasAccessToDima ? "has" : "does not have"} access to
            Backoffice
          </p>
        </div>
        {selectedUserHasAccessToDima && (
          <MUIButton
            label="Save"
            variant="primary"
            size={40}
            isLoading={isSubmitting}
            disabled={
              isSubmitting ||
              UserBackofficeAccessLevel <= AccessLevel.Read ||
              (!isDirty && !accessJustEnabled)
            }
            onClickButton={handleSubmit(onSubmit)}
          />
        )}
      </div>
      {selectedUserHasAccessToDima && <>{appsList}</>}
      <MUIDialog
        id="confirm-revoke-dialog"
        variant="information"
        open={confirmRevokeOpen}
        onClose={setConfirmRevokeOpenFalse}
        heading="Are you sure you want to revoke access to Backoffice?"
        subtitle="If you proceed, the user will no more have access to Backoffice."
        secondaryButtonText="Cancel"
        secondaryButtonConfig={{
          isDisabled: isChangingAccess,
        }}
        primaryButtonText="Revoke access"
        onClickPrimaryButton={onClick}
        isPrimaryButtonDisabled={isChangingAccess}
        primaryButtonConfig={{
          isLoading: isChangingAccess,
          type: "primary",
          size: 32,
          id: "revoke-access-btn",
        }}
      />
    </div>
  )
}
